import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'leaflet/dist/leaflet.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import initializeTheme from './theme';

// Set document title from environment variables (CRA doesn't replace %REACT_APP_*% in HTML)
const scheduleAppName = process.env.REACT_APP_BRAND_SCHEDULE_NAME || 'Light House Beach Management App';
const brandShortName = process.env.REACT_APP_BRAND_SHORT_NAME || process.env.REACT_APP_BRAND_NAME || 'Light House';
if (typeof document !== 'undefined') {
  document.title = scheduleAppName;
  const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitleMeta) {
    appleTitleMeta.setAttribute('content', brandShortName);
  }
}

// Safari debugging
console.log('🍎 Safari debugging - Starting app initialization');
console.log('User Agent:', navigator.userAgent);
console.log('localStorage available:', typeof Storage !== 'undefined');
console.log('Service Worker supported:', 'serviceWorker' in navigator);

// Global error handler for Safari debugging
window.addEventListener('error', (event) => {
  console.error('🚨 Global error caught:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  });
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('🚨 Unhandled promise rejection:', event.reason);
});

const root = ReactDOM.createRoot(document.getElementById('root'));
// Initialize theme from environment variables before rendering
try { initializeTheme(); } catch (e) { console.warn('Theme init failed:', e); }
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Enhanced service worker registration with update handling
if ('serviceWorker' in navigator) {
  const isPublicSchedule = window.location.pathname.startsWith('/public');
  if (isPublicSchedule) {
    // Unregister any existing service workers and clear caches for public pages
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .then(() => caches.keys())
        .then((names) => Promise.all(names.map((n) => caches.delete(n))))
        .then(() => {
          console.log('SW disabled on /public routes and caches cleared');
        })
        .catch((err) => console.warn('Failed to disable SW on /public:', err));
    });
  } else {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'  // Always check for updates
      })
      .then((registration) => {
        console.log('SW registered: ', registration);
        
        // Check for Safari and provide additional logging
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          console.log('Safari detected - Service Worker registered successfully');
        }
        
        // Set up update detection
        setupServiceWorkerUpdateHandling(registration);
        
        // Check for updates periodically (every 30 minutes)
        setInterval(() => {
          console.log('SW: Checking for updates...');
          registration.update();
        }, 30 * 60 * 1000);
        
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
        console.warn('App will continue without service worker functionality');
      });
    });
  }
} else {
  console.log('Service Worker not supported in this browser');
}

// Service worker update handling
function setupServiceWorkerUpdateHandling(registration) {
  // Listen for service worker updates
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    console.log('SW: Update found, new service worker installing...');
    
    if (newWorker) {
      newWorker.addEventListener('statechange', () => {
        console.log('SW: New service worker state:', newWorker.state);
        
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker is ready but waiting
          console.log('SW: New service worker installed and waiting');
          
          // Show update notification to user
          showUpdateNotification();
        }
      });
    }
  });
  
  // Listen for service worker messages
  navigator.serviceWorker.addEventListener('message', (event) => {
    const { data } = event;
    
    if (data.type === 'SW_UPDATED') {
      console.log('SW: Service worker updated to version:', data.version);
      showUpdateSuccessNotification();
    } else if (data.type === 'CACHE_REFRESHED') {
      console.log('SW: Cache refreshed, suggesting page reload');
      showCacheRefreshNotification();
    }
  });
  
  // Listen for controlling service worker changes
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('SW: Controller changed, reloading page...');
    // Only reload if we're not in the middle of a manual refresh
    if (!window.isManualRefresh) {
      window.location.reload();
    }
  });
}

// Update notification functions
function showUpdateNotification() {
  // Check if we already have a notification showing
  if (document.getElementById('sw-update-notification')) {
    return;
  }
  
  const notification = document.createElement('div');
  notification.id = 'sw-update-notification';
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: var(--secondary-color, #2196f3);
    color: white;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    line-height: 1.4;
  `;
  
  notification.innerHTML = `
    <div style="margin-bottom: 12px;">
      <strong>App Update Available</strong><br>
      A new version is ready to install.
    </div>
    <div>
      <button id="update-now-btn" style="
        background: white;
        color: var(--secondary-color, #2196f3);
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        margin-right: 8px;
      ">Update Now</button>
      <button id="update-later-btn" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
      ">Later</button>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  // Handle update now button
  document.getElementById('update-now-btn').addEventListener('click', () => {
    applyServiceWorkerUpdate();
    notification.remove();
  });
  
  // Handle later button
  document.getElementById('update-later-btn').addEventListener('click', () => {
    notification.remove();
  });
  
  // Auto-remove after 30 seconds if no action
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 30000);
}

function showUpdateSuccessNotification() {
  showTemporaryNotification('✅ App updated successfully!', '#4caf50');
}

function showCacheRefreshNotification() {
  const shouldReload = window.confirm('Cache has been refreshed with the latest data. Reload the page to see the changes?');
  if (shouldReload) {
    window.location.reload();
  }
}

function showTemporaryNotification(message, color = 'var(--secondary-color, #2196f3)') {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${color};
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    opacity: 0;
    transform: translateX(100%);
    transition: all 0.3s ease;
  `;
  
  notification.textContent = message;
  document.body.appendChild(notification);
  
  // Animate in
  setTimeout(() => {
    notification.style.opacity = '1';
    notification.style.transform = 'translateX(0)';
  }, 100);
  
  // Animate out and remove
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transform = 'translateX(100%)';
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}

// Apply service worker update
function applyServiceWorkerUpdate() {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
}

// Expose cache refresh function globally for admin use
window.forceCacheRefresh = function() {
  if (!navigator.serviceWorker.controller) {
    console.error('No service worker controller available');
    return Promise.reject(new Error('No service worker available'));
  }
  
  window.isManualRefresh = true;
  
  return new Promise((resolve, reject) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      window.isManualRefresh = false;
      
      if (event.data.success) {
        console.log('Cache refresh successful');
        resolve();
      } else {
        console.error('Cache refresh failed:', event.data.error);
        reject(new Error(event.data.error));
      }
    };
    
    navigator.serviceWorker.controller.postMessage(
      { type: 'FORCE_CACHE_REFRESH' },
      [messageChannel.port2]
    );
  });
};

// Check for iOS home screen app and provide guidance
window.addEventListener('load', () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
  
  if (isIOS && !isStandalone) {
    console.log('📱 iOS detected: For best experience and to receive updates, add this app to your home screen');
  } else if (isIOS && isStandalone) {
    console.log('📱 iOS home screen app detected: Cache updates enabled');
  }
});

reportWebVitals();
